import type { PlotFeature, PlotStyle } from "@plotlibre/core";
import type { PlotLibre } from "@plotlibre/maplibre";
import {
  ASSAULT_DIRECTION_TYPE,
  ATTACK_ARROW_TYPE,
  CURVED_ARROW_TYPE,
  FINE_ARROW_TYPE,
  STRAIGHT_ARROW_TYPE,
  TAILED_ATTACK_ARROW_TYPE,
  TAILED_FINE_ARROW_TYPE,
} from "@plotlibre/symbols";
import type { Map } from "maplibre-gl";

interface PlaygroundElements {
  readonly symbolSelect: HTMLSelectElement;
  readonly drawButton: HTMLButtonElement;
  readonly cancelButton: HTMLButtonElement;
  readonly undoButton: HTMLButtonElement;
  readonly redoButton: HTMLButtonElement;
  readonly deleteButton: HTMLButtonElement;
  readonly clearButton: HTMLButtonElement;
  readonly sampleButton: HTMLButtonElement;
  readonly exportButton: HTMLButtonElement;
  readonly importButton: HTMLButtonElement;
  readonly importInput: HTMLInputElement;
  readonly statusText: HTMLElement;
  readonly plotCount: HTMLElement;
  readonly selectedId: HTMLElement;
  readonly selectionState: HTMLElement;
  readonly fillColor: HTMLInputElement;
  readonly fillOpacity: HTMLInputElement;
  readonly fillOpacityOutput: HTMLOutputElement;
  readonly lineColor: HTMLInputElement;
  readonly lineWidth: HTMLInputElement;
  readonly lineWidthOutput: HTMLOutputElement;
}

export interface PlaygroundAppOptions {
  readonly e2e: boolean;
}

export class PlaygroundApp {
  readonly #map: Map;
  readonly #plot: PlotLibre;
  readonly #elements: PlaygroundElements;
  readonly #e2e: boolean;
  readonly #unsubscribeStore: () => void;
  readonly #unsubscribeSelection: () => void;

  public constructor(map: Map, plot: PlotLibre, options: PlaygroundAppOptions) {
    this.#map = map;
    this.#plot = plot;
    this.#e2e = options.e2e;
    this.#elements = collectElements();
    this.#unsubscribeStore = this.#plot.store.subscribe(() => this.refresh());
    this.#unsubscribeSelection = this.#plot.selection.subscribe(() => this.refresh());
  }

  public start(): void {
    this.#bindToolbar();
    this.#bindStyleControls();
    this.#bindMapRefresh();

    if (!this.#e2e && this.#plot.store.size === 0) {
      this.loadSample();
    } else {
      this.setStatus(
        "准备就绪。点击选择；Shift 添加，Ctrl/Cmd 切换，Alt 移除。",
        "ready",
      );
      this.refresh();
    }
  }

  public destroy(): void {
    this.#unsubscribeSelection();
    this.#unsubscribeStore();
  }

  public refresh(): void {
    const selected = this.#selectedFeature();
    const selectedCount = this.#plot.selectedIds.length;
    const isDrawing = this.#plot.interaction.isDrawing;
    const plotCount = this.#plot.store.size;

    this.#elements.plotCount.textContent = `${plotCount} 个标绘`;
    this.#elements.selectedId.textContent =
      selectedCount === 0
        ? "未选择"
        : selectedCount === 1
          ? selected?.id ?? "未选择"
          : `${selected?.id ?? "未知"}（主对象）`;
    this.#elements.selectionState.textContent =
      selectedCount === 0 ? "未选择" : `已选择 ${selectedCount} 个`;
    this.#elements.selectionState.dataset.state =
      selectedCount > 0 ? "active" : "idle";

    this.#elements.symbolSelect.disabled = isDrawing;
    this.#elements.drawButton.disabled = isDrawing;
    this.#elements.cancelButton.disabled = !isDrawing;
    this.#elements.undoButton.disabled = !this.#plot.history.canUndo;
    this.#elements.redoButton.disabled = !this.#plot.history.canRedo;
    this.#elements.deleteButton.disabled = selectedCount === 0;
    this.#elements.clearButton.disabled = plotCount === 0;
    this.#elements.exportButton.disabled = plotCount === 0;

    for (const input of [
      this.#elements.fillColor,
      this.#elements.fillOpacity,
      this.#elements.lineColor,
      this.#elements.lineWidth,
    ]) {
      input.disabled = selected === undefined;
    }

    if (selected) {
      this.#syncStyleInputs(selected);
    }
  }

  public loadSample(): void {
    this.#plot.clear();

    const sampleStyle: PlotStyle = {
      fillColor: "#e54b4b",
      fillOpacity: 0.46,
      lineColor: "#8d2020",
      lineWidth: 2,
    };

    const samples = [
      {
        id: "sample-main-direction",
        plotType: STRAIGHT_ARROW_TYPE,
        controlPoints: [
          [118.755, 32.035],
          [118.835, 32.095],
        ] as const,
        style: sampleStyle,
      },
      {
        id: "sample-fine-direction",
        plotType: FINE_ARROW_TYPE,
        controlPoints: [
          [118.77, 32.105],
          [118.855, 32.065],
        ] as const,
        style: {
          fillColor: "#f29e38",
          fillOpacity: 0.56,
          lineColor: "#9a5512",
          lineWidth: 2,
        },
      },
      {
        id: "sample-tailed-fine-direction",
        plotType: TAILED_FINE_ARROW_TYPE,
        controlPoints: [
          [118.735, 32.075],
          [118.795, 32.125],
        ] as const,
        style: {
          fillColor: "#2d9cdb",
          fillOpacity: 0.46,
          lineColor: "#14608b",
          lineWidth: 2,
        },
      },
      {
        id: "sample-assault-direction",
        plotType: ASSAULT_DIRECTION_TYPE,
        controlPoints: [
          [118.805, 32.025],
          [118.885, 32.055],
        ] as const,
        style: {
          fillColor: "#9b51e0",
          fillOpacity: 0.5,
          lineColor: "#5f2b91",
          lineWidth: 2,
        },
      },
      {
        id: "sample-curved-direction",
        plotType: CURVED_ARROW_TYPE,
        controlPoints: [
          [118.72, 32.02],
          [118.75, 32.05],
          [118.78, 32.1],
          [118.82, 32.14],
        ] as const,
        style: {
          fillColor: "#26a69a",
          fillOpacity: 0.52,
          lineColor: "#116a63",
          lineWidth: 2,
        },
      },
      {
        id: "sample-attack-direction",
        plotType: ATTACK_ARROW_TYPE,
        controlPoints: [
          [118.845, 32.095],
          [118.857, 32.09],
          [118.882, 32.118],
          [118.915, 32.15],
        ] as const,
        style: {
          fillColor: "#e4578c",
          fillOpacity: 0.52,
          lineColor: "#8f2853",
          lineWidth: 2,
        },
      },
      {
        id: "sample-tailed-attack-direction",
        plotType: TAILED_ATTACK_ARROW_TYPE,
        controlPoints: [
          [118.695, 32.105],
          [118.707, 32.1],
          [118.732, 32.13],
          [118.77, 32.165],
        ] as const,
        style: {
          fillColor: "#f2c94c",
          fillOpacity: 0.54,
          lineColor: "#8a6812",
          lineWidth: 2,
        },
      },
    ];

    for (const sample of samples) {
      this.#plot.create({
        id: sample.id,
        plotType: sample.plotType,
        controlPoints: sample.controlPoints,
        style: sample.style,
        metadata: { source: "PlotLibre playground sample" },
      });
    }

    this.#plot.history.clear();
    this.#plot.select(samples[0]?.id);
    this.#map.fitBounds(
      [
        [118.67, 31.99],
        [118.94, 32.19],
      ],
      { padding: 72, duration: 500 },
    );
    this.setStatus(
      "已加载南京七类箭头示例。可按 Shift 多选并拖动任一选中对象整体移动。",
      "ready",
    );
    this.refresh();
  }

  #bindToolbar(): void {
    this.#elements.symbolSelect.addEventListener("change", () => {
      this.setStatus(this.#selectionInstruction(), "ready");
    });

    this.#elements.drawButton.addEventListener("click", () => {
      try {
        const plotType = this.#elements.symbolSelect.value;
        this.#plot.draw(plotType, { style: this.#currentInputStyle() });
        this.setStatus(this.#drawingInstruction(), "drawing");
        this.refresh();
      } catch (error) {
        this.#reportError("无法开始绘制", error);
      }
    });

    this.#elements.cancelButton.addEventListener("click", () => {
      const cancelled = this.#plot.cancelDrawing();
      this.setStatus(
        cancelled ? "已取消当前绘制。" : "当前没有正在进行的绘制。",
        "ready",
      );
      this.refresh();
    });

    this.#elements.undoButton.addEventListener("click", () => {
      const changed = this.#plot.undo();
      this.setStatus(changed ? "已撤销上一步操作。" : "没有可撤销的操作。", "ready");
      this.refresh();
    });

    this.#elements.redoButton.addEventListener("click", () => {
      const changed = this.#plot.redo();
      this.setStatus(changed ? "已重做操作。" : "没有可重做的操作。", "ready");
      this.refresh();
    });

    this.#elements.deleteButton.addEventListener("click", () => {
      const selectedCount = this.#plot.selectedIds.length;
      if (selectedCount === 0) return;
      const changed = this.#plot.removeSelected();
      this.setStatus(
        changed
          ? `已原子删除 ${selectedCount} 个选中对象。`
          : "当前没有可删除的选中对象。",
        "ready",
      );
      this.refresh();
    });

    this.#elements.clearButton.addEventListener("click", () => {
      this.#plot.clear();
      this.setStatus("已清空当前文档。", "ready");
      this.refresh();
    });

    this.#elements.sampleButton.addEventListener("click", () => this.loadSample());
    this.#elements.exportButton.addEventListener("click", () => this.#exportDocument());
    this.#elements.importButton.addEventListener("click", () => this.#elements.importInput.click());
    this.#elements.importInput.addEventListener("change", () => void this.#importDocument());
  }

  #bindStyleControls(): void {
    this.#elements.fillOpacity.addEventListener("input", () => {
      this.#elements.fillOpacityOutput.value = Number(
        this.#elements.fillOpacity.value,
      ).toFixed(2);
    });
    this.#elements.lineWidth.addEventListener("input", () => {
      this.#elements.lineWidthOutput.value = `${this.#elements.lineWidth.value} px`;
    });

    this.#elements.fillColor.addEventListener("change", () => {
      this.#replaceSelectedStyle({ fillColor: this.#elements.fillColor.value });
    });
    this.#elements.fillOpacity.addEventListener("change", () => {
      this.#replaceSelectedStyle({
        fillOpacity: Number(this.#elements.fillOpacity.value),
      });
    });
    this.#elements.lineColor.addEventListener("change", () => {
      this.#replaceSelectedStyle({ lineColor: this.#elements.lineColor.value });
    });
    this.#elements.lineWidth.addEventListener("change", () => {
      this.#replaceSelectedStyle({
        lineWidth: Number(this.#elements.lineWidth.value),
      });
    });
  }

  #bindMapRefresh(): void {
    const deferredRefresh = (): void => {
      queueMicrotask(() => {
        if (this.#plot.interaction.isDrawing) {
          this.setStatus(this.#drawingProgressInstruction(), "drawing");
        } else if (this.#plot.translation.isTranslating) {
          this.setStatus(
            `正在整体移动 ${this.#plot.selectedIds.length} 个对象；Escape 取消。`,
            "selected",
          );
        } else if (this.#plot.transformRejection) {
          this.setStatus(
            `整体移动被拒绝：${this.#plot.transformRejection.message}`,
            "error",
          );
        } else if (this.#plot.selectedIds.length > 1) {
          this.setStatus(
            `已选择 ${this.#plot.selectedIds.length} 个对象。拖动对象整体移动；Delete 批量删除。`,
            "selected",
          );
        } else if (this.#plot.selectedId) {
          this.setStatus(
            "对象已选择。拖动对象整体移动；拖动圆形控制点编辑 primary。",
            "selected",
          );
        }
        this.refresh();
      });
    };

    this.#map.on("click", deferredRefresh);
    this.#map.on("dblclick", deferredRefresh);
    this.#map.on("mousemove", deferredRefresh);
    this.#map.on("mouseup", deferredRefresh);
    this.#map.on("style.load", deferredRefresh);
    this.#map.getCanvas().addEventListener("keydown", deferredRefresh);
  }

  #replaceSelectedStyle(patch: PlotStyle): void {
    const selected = this.#selectedFeature();
    if (!selected) return;

    this.#plot.replace({
      ...selected,
      style: {
        ...selected.style,
        ...patch,
      },
    });
    this.setStatus("已更新 primary 对象样式。", "selected");
    this.refresh();
  }

  #selectedFeature(): PlotFeature | undefined {
    const selectedId = this.#plot.selectedId;
    return selectedId ? this.#plot.store.find(selectedId) : undefined;
  }

  #selectedSymbolLabel(): string {
    return this.#elements.symbolSelect.selectedOptions[0]?.textContent?.trim() ??
      this.#elements.symbolSelect.value;
  }

  #selectedPlotType(): string {
    return this.#elements.symbolSelect.value;
  }

  #selectionInstruction(): string {
    switch (this.#selectedPlotType()) {
      case CURVED_ARROW_TYPE:
        return "已选择曲线箭头。第一个点为尾部中心，后续点定义路径；双击或 Enter 完成。";
      case ATTACK_ARROW_TYPE:
        return "已选择攻击箭头。前两个点定义尾缘宽度，后续点定义进攻骨架和目标。";
      case TAILED_ATTACK_ARROW_TYPE:
        return "已选择燕尾攻击箭头。控制点与攻击箭头一致，尾部使用独立内凹燕尾。";
      default:
        return `已选择${this.#selectedSymbolLabel()}。点击“开始绘制”后确定箭尾和箭尖。`;
    }
  }

  #drawingInstruction(): string {
    switch (this.#selectedPlotType()) {
      case CURVED_ARROW_TYPE:
        return "正在绘制曲线箭头：点击尾部中心与路径点；第三个候选点开始预览，双击或 Enter 完成。";
      case ATTACK_ARROW_TYPE:
        return "正在绘制攻击箭头：先点击两个尾缘点，再点击进攻骨架；双击目标点或 Enter 完成。";
      case TAILED_ATTACK_ARROW_TYPE:
        return "正在绘制燕尾攻击箭头：先点击两个尾缘点，再点击进攻骨架；双击目标点或 Enter 完成。";
      default:
        return `正在绘制${this.#selectedSymbolLabel()}：请点击箭尾，再移动鼠标并点击箭尖。`;
    }
  }

  #drawingProgressInstruction(): string {
    switch (this.#selectedPlotType()) {
      case CURVED_ARROW_TYPE:
        return "曲线箭头绘制中：继续点击路径点，双击最后一点或按 Enter 完成；Backspace 退点。";
      case ATTACK_ARROW_TYPE:
        return "攻击箭头绘制中：前两点是尾缘，继续点击骨架/目标；双击或 Enter 完成。";
      case TAILED_ATTACK_ARROW_TYPE:
        return "燕尾攻击箭头绘制中：前两点是尾缘，继续点击骨架/目标；双击或 Enter 完成。";
      default:
        return "绘制中：再次点击地图完成；Escape 取消。";
    }
  }

  #syncStyleInputs(feature: PlotFeature): void {
    const fillColor = feature.style.fillColor ?? "#d32f2f";
    const fillOpacity = feature.style.fillOpacity ?? 0.45;
    const lineColor = feature.style.lineColor ?? "#8e0000";
    const lineWidth = feature.style.lineWidth ?? 2;

    this.#elements.fillColor.value = fillColor;
    this.#elements.fillOpacity.value = String(fillOpacity);
    this.#elements.fillOpacityOutput.value = fillOpacity.toFixed(2);
    this.#elements.lineColor.value = lineColor;
    this.#elements.lineWidth.value = String(lineWidth);
    this.#elements.lineWidthOutput.value = `${lineWidth} px`;
  }

  #currentInputStyle(): PlotStyle {
    return {
      fillColor: this.#elements.fillColor.value,
      fillOpacity: Number(this.#elements.fillOpacity.value),
      lineColor: this.#elements.lineColor.value,
      lineWidth: Number(this.#elements.lineWidth.value),
    };
  }

  #exportDocument(): void {
    const json = this.#plot.exportJson("plotlibre-playground", "PlotLibre Playground");
    const blob = new Blob([json], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "plotlibre-playground.plotjson.json";
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    this.setStatus("PlotJSON 已导出。", "ready");
  }

  async #importDocument(): Promise<void> {
    const file = this.#elements.importInput.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const documentData = this.#plot.importDocument(text);
      this.setStatus(
        `已导入“${documentData.name}”，共 ${documentData.features.length} 个标绘。`,
        "ready",
      );
    } catch (error) {
      this.#reportError("PlotJSON 导入失败", error);
    } finally {
      this.#elements.importInput.value = "";
      this.refresh();
    }
  }

  private setStatus(message: string, state: string): void {
    this.#elements.statusText.textContent = message;
    this.#elements.statusText.dataset.state = state;
  }

  #reportError(prefix: string, error: unknown): void {
    const detail = error instanceof Error ? error.message : String(error);
    this.setStatus(`${prefix}：${detail}`, "error");
    console.error(prefix, error);
  }
}

function collectElements(): PlaygroundElements {
  return {
    symbolSelect: required("symbol-select", HTMLSelectElement),
    drawButton: required("draw-button", HTMLButtonElement),
    cancelButton: required("cancel-button", HTMLButtonElement),
    undoButton: required("undo-button", HTMLButtonElement),
    redoButton: required("redo-button", HTMLButtonElement),
    deleteButton: required("delete-button", HTMLButtonElement),
    clearButton: required("clear-button", HTMLButtonElement),
    sampleButton: required("sample-button", HTMLButtonElement),
    exportButton: required("export-button", HTMLButtonElement),
    importButton: required("import-button", HTMLButtonElement),
    importInput: required("import-input", HTMLInputElement),
    statusText: required("status-text", HTMLElement),
    plotCount: required("plot-count", HTMLElement),
    selectedId: required("selected-id", HTMLElement),
    selectionState: required("selection-state", HTMLElement),
    fillColor: required("fill-color", HTMLInputElement),
    fillOpacity: required("fill-opacity", HTMLInputElement),
    fillOpacityOutput: required("fill-opacity-output", HTMLOutputElement),
    lineColor: required("line-color", HTMLInputElement),
    lineWidth: required("line-width", HTMLInputElement),
    lineWidthOutput: required("line-width-output", HTMLOutputElement),
  };
}

function required<T extends HTMLElement>(
  id: string,
  constructor: { new (): T },
): T {
  const element = document.getElementById(id);
  if (!(element instanceof constructor)) {
    throw new Error(`Expected #${id} to be ${constructor.name}.`);
  }
  return element;
}
